import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { FilterCriteria } from '@woolworthsnz/trader-api';
import {
	ButtonComponent,
	ModalOverlayService,
	ModalOverlayRef,
	SlottedModalComponent,
	SvgIconComponent,
	SVGIcon,
	ModalOverlayState,
} from '@woolworthsnz/styleguide';
import { FilterDisclaimerComponent } from '../filter-disclaimer/filter-disclaimer.component';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { UrlSegment } from '@angular/router';
import { SearchFilterKeys } from '../enums';

interface FilterChangeArgs {
	filter: FilterCriteria;
	isSelected: boolean;
	url: UrlSegment[];
}

@Component({
	selector: 'search-filter',
	standalone: true,
	imports: [
		CommonModule,
		PortalModule,
		ButtonComponent,
		SlottedModalComponent,
		FormsModule,
		FilterDisclaimerComponent,
		SvgIconComponent,
	],
	templateUrl: './search-filter.component.html',
	styleUrls: ['./search-filter.component.scss'],
})
export class SearchFilterComponent implements OnChanges {
	@Input() filterTitle = '';
	@Input() filterOptions: Array<FilterChangeArgs> = [];
	@Input() optionsRevealApproach: 'dropdown' | 'modal' = 'dropdown';

	@Output() filterChange = new EventEmitter<Array<FilterChangeArgs>>();

	@ViewChild('filterTrigger', { read: ElementRef }) fitlerTrigger: ElementRef<HTMLElement>;
	@ViewChild('filterModal') filterOverlayTemplate: CdkPortal;
	@ViewChild('filterDropdown') filterDropdownTemplate: CdkPortal;

	isFilterOpen$ = new BehaviorSubject<boolean>(false);
	nextFilterOptions: Array<FilterChangeArgs> = [];

	private _initSelectedFilterOptionsNum = 0;
	private _filterModalRef?: ModalOverlayRef;
	private _filterDropdownRef?: ModalOverlayRef;

	constructor(private modalOverlayService: ModalOverlayService) {}

	get sorterIcon(): Observable<SVGIcon> {
		return this.isFilterOpen$.pipe(map((up) => this.getChevronIcon(up)));
	}

	get isAnyFilterOptionSelected(): boolean {
		return this.nextFilterOptions.some(({ isSelected }) => isSelected);
	}

	get isApplyFiltersDisabled(): boolean {
		return this._initSelectedFilterOptionsNum === 0 && !this.isAnyFilterOptionSelected;
	}

	get selectedFiltersNum(): number {
		return this.filterOptions.reduce((num, option) => num + (option.isSelected ? 1 : 0), 0);
	}

	trackByOptionNameFn = (_: number, option: FilterChangeArgs): string | undefined => option.filter.name;

	getChevronIcon(up: boolean): SVGIcon {
		return up ? SVGIcon.ChevronTop : SVGIcon.ChevronBottom;
	}

	openFilter(): void {
		this.isFilterOpen$.next(true);
		this.nextFilterOptions = this.filterOptions;
		this._initSelectedFilterOptionsNum = this.nextFilterOptions.filter(({ isSelected }) => isSelected).length;

		if (this.optionsRevealApproach === 'dropdown') {
			this.modalOverlayService.setState({
				element: this.fitlerTrigger,
				isConnectedElement: true,
				backdropClass: 'overlay--transparent',
				scrollStrategy: 'block',
				width: 'auto',
				maxWidth: 224,
			});

			this._filterDropdownRef = this.modalOverlayService.open({
				templateRef: this.filterDropdownTemplate,
				skipTracking: true,
			});

			this._filterDropdownRef?.detachments().subscribe(() => {
				this.isFilterOpen$.next(false);
			});
		} else {
			this.modalOverlayService.setState({
				isConnectedElement: false,
			});
			this._filterModalRef = this.modalOverlayService.open({
				templateRef: this.filterOverlayTemplate,
				skipTracking: true,
			});

			this._filterModalRef?.detachments().subscribe(() => {
				this.isFilterOpen$.next(false);
			});
		}
	}

	onCloseFilterModal(): void {
		this.closeAllModalAndDropdown();
	}

	canShowDisclaimer = (filter: FilterCriteria): boolean => filter?.key === SearchFilterKeys.PiesAttributes;

	applyFilters(): void {
		this.filterChange.emit(this.nextFilterOptions);
		this.closeAllModalAndDropdown();
	}

	clearFilters(): void {
		this.nextFilterOptions = this.nextFilterOptions.map((option) => ({
			...option,
			isSelected: false,
		}));
		this.filterChange.emit(this.nextFilterOptions);
		this.closeAllModalAndDropdown();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.hasOwnProperty('optionsRevealApproach')) {
			this.closeAllModalAndDropdown();
		}
	}

	public closeAllModalAndDropdown(): void {
		this._filterDropdownRef?.close();
		this._filterModalRef?.close();
		this.modalOverlayService.setState(new ModalOverlayState());
	}
}
